import { Button, Divider, Group, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Fragment, useEffect } from "react";
import { useOpenChurchApp, type Membership } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { PageHeader } from "@how/ui";
import { env } from "../lib/env";

/**
 * The staff address for a login with no staff church but with member churches (ADR 0068): it says the address is for
 * staff and opens each church's member portal through the hand-off (ADR 0067), so nobody signs in twice. The churches
 * are plain rows inside the door's panel (DoorLayout), not cards nested in it. `title` and `description` let the same
 * page say why a staff church cannot be opened (I1) or that one church's address is for its staff (P14). `path` opens
 * My schedule there for an old `/me/schedule` link (ADR 0070 R10).
 */
export function StaffOnlyPage({
  churches,
  title = "This address is for church staff",
  description = "Your email has no staff access at a church on HoW. Open your church's member portal instead: your serving dates are there, under My schedule.",
  path = "/",
}: {
  readonly churches: readonly Membership[];
  readonly title?: string;
  readonly description?: string;
  /** The page each portal opens at, "/" by default; "/my-schedule" when an old My schedule link lands here. */
  readonly path?: string;
}) {
  // The church's preferred address for its portal (R9, ADR 0067 A8); HoW's when it has none live.
  const openApp = useOpenChurchApp(env);
  const failed = openApp.error !== null;
  useEffect(() => {
    if (failed) {
      notifications.show({
        color: semanticColor("danger"),
        title: "Could not open that address",
        message: "That address could not be opened. Try again.",
      });
    }
  }, [failed]);
  return (
    <>
      <PageHeader title={title} description={description} />
      <Stack gap="sm" mt="md">
        {churches.map((m, i) => (
          <Fragment key={m.tenantId}>
            {i === 0 ? null : <Divider />}
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Text fw={600}>{m.name}</Text>
              <Button
                aria-label={`Open the member portal: ${m.name}`}
                onClick={() => {
                  openApp.open({ tenantId: m.tenantId, slug: m.slug, app: "member_portal", path });
                }}
              >
                Open the member portal
              </Button>
            </Group>
          </Fragment>
        ))}
      </Stack>
    </>
  );
}
